/**
 * Модалка шаблона (создание / редактирование / просмотр).
 * Переиспользует формы создания: TaskFormTab и ProjectFormFields
 * в режиме isTemplate={true}. Это гарантирует идентичный интерфейс
 * и автоматическое обновление при изменении форм создания.
 * Поля, исключённые из шаблонов, скрываются внутри этих компонентов.
 */
import { useCallback, useMemo, useState } from "react";
import { ModalShell } from "../ModalShell";
import { Tabs } from "../Tabs";
import { FormField } from "../FormField";
import { ToggleSwitch } from "../ToggleSwitch";
import { useForm } from "../../hooks/useForm";
import { useAsyncSubmit } from "../../hooks/useAsyncSubmit";
import { usePriorityOptions } from "../../hooks/useMetaHelpers";
import { useAuth } from "../../hooks/useAuth";
import { useWorkloadDb } from "../../hooks/useDb";
import { useAssignableEmployeesQuery } from "../../api/queries/employees";
import { useDictionariesQuery } from "../../api/queries/dictionaries";
import {
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
} from "../../api/queries/templates";
import {
  TEMPLATE_KINDS,
  TEMPLATE_KIND_LABELS,
} from "../../utils/templateSchemas";
import { countNestedTasks } from "../../utils/templateNesting";
import { parseTagsInput } from "../../utils/tags";
import { unitOptionsForPtype } from "../../utils/projectUnits";
import { PRIORITIES } from "../../utils/constants";
import { useNestedModalEscape } from "./useNestedModalEscape";
import TaskDraftModal from "./TaskDraftModal";
import DraftListItem from "./DraftListItem";
import { TaskFormTab } from "../Modals/TaskModal/TaskFormTab";
import { ProjectFormFields } from "../Modals/ProjectModal/ProjectFormFields";

const MAX_NAME_LENGTH = 80;
const FIELDS = Object.freeze(["name", "isShared"]);
const EMPTY_LIST = Object.freeze([]);

const MODES = Object.freeze({
  create: "create",
  edit: "edit",
  view: "view",
});

const createEmptyNestedNode = (singular) => ({
  title: `Новая ${singular}`,
  priority: "mid",
  plannedHours: 8,
});

const getNodeByPath = (items, path) => {
  let current = items[path[0]];
  for (let i = 1; i < path.length; i++) {
    if (!current?.subtasks) return null;
    current = current.subtasks[path[i]];
  }
  return current;
};

const updateNodeByPath = (items, path, updater) => {
  const [head, ...tail] = path;
  return items.map((item, idx) => {
    if (idx !== head) return item;
    if (tail.length === 0) return updater(item);
    return {
      ...item,
      subtasks: updateNodeByPath(item.subtasks || [], tail, updater),
    };
  });
};

const deleteNodeByPath = (items, path) => {
  const [head, ...tail] = path;
  if (tail.length === 0) return items.filter((_, idx) => idx !== head);
  return items.map((item, idx) => {
    if (idx !== head) return item;
    return {
      ...item,
      subtasks: deleteNodeByPath(item.subtasks || [], tail),
    };
  });
};

const cleanNestedTree = (items) => {
  if (!Array.isArray(items)) return items;
  return items.map((node) => {
    const next = { ...node };
    if (next.tagsInput !== undefined) {
      const parsed = parseTagsInput(next.tagsInput).valid;
      if (parsed.length > 0) next.tags = parsed;
      else delete next.tags;
      delete next.tagsInput;
    }
    if (next.subtasks) next.subtasks = cleanNestedTree(next.subtasks);
    return next;
  });
};

const TASK_PRIORITY_OPTIONS = Object.entries(PRIORITIES).map(([k, v]) => ({
  value: k,
  label: v.label,
}));

export default function TemplateModal({
  mode = MODES.create,
  kind: propKind,
  source,
  nested = [],
  template = null,
  onClose,
  toast,
}) {
  useNestedModalEscape(onClose);

  const createMutation = useCreateTemplateMutation();
  const updateMutation = useUpdateTemplateMutation();

  const { capabilities } = useAuth();
  const canSeeEconomics = Boolean(capabilities?.seeEconomics);
  const db = useWorkloadDb() ?? {};
  const kbs = db.kbs ?? EMPTY_LIST;
  const departments = db.departments ?? EMPTY_LIST;
  const projects = db.projects ?? EMPTY_LIST;
  const employees = db.employees ?? EMPTY_LIST;

  const { data: assignable = EMPTY_LIST } = useAssignableEmployeesQuery();
  const { data: dictionaries = {} } = useDictionariesQuery();

  const customers = useMemo(
    () => dictionaries.customers || [],
    [dictionaries.customers],
  );
  const categories = useMemo(
    () => dictionaries.project_categories || [],
    [dictionaries.project_categories],
  );
  const aircraftTypes = useMemo(() => {
    const set = new Set(dictionaries.aircraft_types || []);
    projects.forEach((p) => {
      if (p.aircraftType) set.add(p.aircraftType.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [dictionaries.aircraft_types, projects]);

  const aircraftTypeOptions = useMemo(
    () => aircraftTypes.map((v) => ({ value: v, label: v })),
    [aircraftTypes],
  );
  const customerOptions = useMemo(
    () => customers.map((v) => ({ value: v, label: v })),
    [customers],
  );
  const categoryOptions = useMemo(
    () => categories.map((v) => ({ value: v, label: v })),
    [categories],
  );

  const projectOptions = useMemo(
    () => [
      { value: "", label: "- Выберите проект -" },
      ...projects
        .filter((p) => p.status === "active" && !p.archived)
        .map((p) => ({
          value: p.id,
          label: `${p.code} - ${p.name}${p.ptype === "admin" ? " (административный)" : ""}`,
        })),
    ],
    [projects],
  );

  const assigneeOptions = useMemo(
    () => [
      { value: "", label: "- Выберите исполнителя -" },
      ...assignable.map((e) => ({
        value: e.id,
        label: `${e.last} ${e.first}`,
      })),
    ],
    [assignable],
  );

  const employeeOptions = useMemo(
    () => [
      { value: "", label: "- Выберите ответственного -" },
      ...employees
        .filter((e) => !e.fired)
        .map((e) => ({ value: e.id, label: `${e.last} ${e.first}` })),
    ],
    [employees],
  );

  const isCreate = mode === MODES.create;
  const isEdit = mode === MODES.edit;
  const isView = mode === MODES.view;
  const readOnly = isView;

  const effectiveKind = template?.kind || propKind;
  const kindMeta = TEMPLATE_KINDS[effectiveKind];
  const nestedKey = kindMeta?.nestedKey;
  const nestedLabel = kindMeta?.nestedLabel || "вложенные элементы";
  const nestedSingular = kindMeta?.nestedSingular || "элемент";
  const kindLabel = TEMPLATE_KIND_LABELS[effectiveKind] || "сущности";

  const templatePayload = template?.payload;

  const initialPayload = useMemo(() => {
    const base = isCreate
      ? { ...(source || {}) }
      : { ...(templatePayload || {}) };
    if (isCreate && nested.length && nestedKey) base[nestedKey] = nested;
    if (Array.isArray(base.tags)) {
      base.tagsInput = base.tags.join(", ");
      delete base.tags;
    }
    return base;
  }, [isCreate, source, nested, nestedKey, templatePayload]);

  const [payload, setPayload] = useState(initialPayload);
  const [includeNested, setIncludeNested] = useState(
    () =>
      Array.isArray(initialPayload[nestedKey]) &&
      initialPayload[nestedKey].length > 0,
  );
  const [activeTab, setActiveTab] = useState("info");
  const [editingPath, setEditingPath] = useState(null);

  const priorityOptions = usePriorityOptions(payload.ptype);

  const unitOptions = useMemo(
    () => unitOptionsForPtype(payload.ptype ?? "prod", { kbs, departments }),
    [payload.ptype, kbs, departments],
  );

  const nestedItems = useMemo(
    () => (Array.isArray(payload[nestedKey]) ? payload[nestedKey] : []),
    [payload, nestedKey],
  );

  const initialValues = useMemo(
    () => ({
      name: template?.name || "",
      isShared: template?.isShared || false,
    }),
    [template],
  );

  const validate = useCallback((vals) => {
    const errors = {};
    const name = String(vals.name || "").trim();
    if (!name) errors.name = "Укажите название";
    else if (name.length > MAX_NAME_LENGTH)
      errors.name = `Не более ${MAX_NAME_LENGTH} символов`;
    return errors;
  }, []);

  const setPayloadField = useCallback((field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setNested = useCallback(
    (next) => {
      setPayload((prev) => {
        const copy = { ...prev };
        if (next.length) copy[nestedKey] = next;
        else delete copy[nestedKey];
        return copy;
      });
    },
    [nestedKey],
  );

  const saveAsync = useCallback(
    async (vals) => {
      const payloadToSave = { ...payload };

      if (payloadToSave.tagsInput !== undefined) {
        const parsed = parseTagsInput(payloadToSave.tagsInput).valid;
        if (parsed.length > 0) payloadToSave.tags = parsed;
        else delete payloadToSave.tags;
        delete payloadToSave.tagsInput;
      }

      if (nestedKey && payloadToSave[nestedKey]) {
        payloadToSave[nestedKey] = cleanNestedTree(payloadToSave[nestedKey]);
      }

      if (isEdit) {
        await updateMutation.mutateAsync({
          id: template.id,
          body: {
            name: vals.name,
            isShared: vals.isShared,
            payload: payloadToSave,
          },
        });
      } else if (isCreate) {
        const payloadSource = { ...payloadToSave };
        if (nestedKey && !includeNested) delete payloadSource[nestedKey];
        await createMutation.mutateAsync({
          kind: effectiveKind,
          name: vals.name,
          isShared: vals.isShared,
          payload: payloadSource,
        });
      }
      onClose();
    },
    [
      isEdit,
      isCreate,
      template,
      payload,
      includeNested,
      nestedKey,
      effectiveKind,
      createMutation,
      updateMutation,
      onClose,
    ],
  );

  const { values, handleChange, handleSubmit, errors, touched } = useForm(
    initialValues,
    validate,
    { fields: FIELDS },
  );

  const { submit, isSubmitting } = useAsyncSubmit(saveAsync, (error) => {
    toast(error.message || "Не удалось сохранить шаблон", "error");
  });

  const title = isView
    ? `Шаблон: ${template?.name || ""}`
    : isEdit
      ? `Редактирование шаблона ${kindLabel}`
      : `Сохранить как шаблон ${kindLabel}`;

  const saveLabel = isEdit ? "Сохранить" : "Создать";

  const footer = isView ? (
    <div className="modal-foot">
      <div className="spacer" />
      <button className="btn ghost" onClick={onClose}>
        Закрыть
      </button>
    </div>
  ) : null;

  const nestedCount = countNestedTasks(nestedItems);

  const addNestedNode = () => {
    setNested([...nestedItems, createEmptyNestedNode(nestedSingular)]);
    if (isCreate && !includeNested) setIncludeNested(true);
  };

  const tabs = [
    { id: "info", label: "Информация" },
    ...(nestedKey
      ? [{ id: "nested", label: `${nestedLabel} (${nestedCount})` }]
      : []),
  ];

  const isAdminProject = payload.ptype === "admin";

  const renderInfoTab = () => (
    <>
      {isView && (
        <div className="info-box">
          Это общий шаблон. Менять его содержимое может только владелец.
        </div>
      )}

      <FormField
        label="Название шаблона"
        required
        value={values.name}
        onChange={(v) => handleChange("name", v)}
        error={touched.name && errors.name}
        disabled={readOnly}
        inline
      />

      <div className="field-row">
        <label className="field-label">Общий шаблон</label>
        <div className="flex-1 flex items-center">
          <ToggleSwitch
            checked={values.isShared}
            onChange={(v) => handleChange("isShared", v)}
            label={
              values.isShared
                ? "Доступен всем сотрудникам"
                : "Личный - видите только вы"
            }
            disabled={readOnly}
          />
        </div>
      </div>

      {effectiveKind === "project" ? (
        <ProjectFormFields
          form={{
            values: payload,
            handleChange: setPayloadField,
            errors: {},
            touched: {},
          }}
          access={{
            canEditFields: !readOnly,
            isAdminProject,
            canChangeStatus: false,
            canEditContractNumber: false,
            canEditHourlyRate: false,
            canChangeManager: !readOnly,
            canSeeEconomicsFields: canSeeEconomics,
          }}
          options={{
            customerOptions,
            aircraftTypeOptions,
            categoryOptions,
            priorityOptions,
            unitOptions,
            employeeOptions,
            statusOptions: [],
            openTag: null,
          }}
          isTemplate={true}
        />
      ) : (
        <TaskFormTab
          form={{
            values: payload,
            handleChange: setPayloadField,
            updateValues: (patch) =>
              setPayload((prev) => ({ ...prev, ...patch })),
            errors: {},
            touched: {},
          }}
          access={{
            canEditFields: !readOnly,
            isProjectLocked: false,
            isAdminProject: false,
            canToggleFlags: false,
          }}
          options={{
            projectOptions,
            assigneeOptionsList: assigneeOptions,
            priorityOptions: TASK_PRIORITY_OPTIONS,
            statusOptions: [],
            dependencyOptions: [],
            dependencyTypeOptions: [],
          }}
          template={{
            isNew: false,
            isCopy: false,
            appliedTemplateName: null,
            onApply: () => {},
          }}
          summary={{ checked: false, disabled: true }}
          isTemplate={true}
        />
      )}
    </>
  );

  const renderNestedItems = (items, parentPath = [], level = 0) =>
    items.map((node, idx) => {
      const currentPath = [...parentPath, idx];
      return (
        <div
          key={currentPath.join("-")}
          className="template-tree-node"
          data-depth={Math.min(level, 9)}
        >
          <DraftListItem
            node={node}
            onClick={() => setEditingPath(currentPath)}
          />
          {node.subtasks &&
            node.subtasks.length > 0 &&
            renderNestedItems(node.subtasks, currentPath, level + 1)}
        </div>
      );
    });

  const renderNestedTab = () => (
    <>
      <div className="template-tree-header">
        <div className="template-tree-title">
          {nestedLabel} ({nestedCount})
        </div>
        {!readOnly && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={addNestedNode}
          >
            + {nestedSingular}
          </button>
        )}
      </div>

      {isCreate && nestedItems.length > 0 && (
        <div className="field-row">
          <label className="field-label">Сохранение</label>
          <div className="flex-1 flex items-center">
            <ToggleSwitch
              checked={includeNested}
              onChange={(v) => setIncludeNested(v)}
              label="Включить в шаблон при сохранении"
            />
          </div>
        </div>
      )}

      {nestedItems.length === 0 ? (
        <div className="mut sm">Вложенных элементов нет</div>
      ) : (
        <div className="template-draft-list">
          {renderNestedItems(nestedItems)}
        </div>
      )}
    </>
  );

  const activeNode = editingPath
    ? getNodeByPath(nestedItems, editingPath)
    : null;

  return (
    <>
      <ModalShell
        title={title}
        onClose={onClose}
        onSave={isView ? undefined : handleSubmit(submit)}
        saveLabel={saveLabel}
        showSave={!isView}
        saveDisabled={isSubmitting}
        width={620}
        footer={footer}
      >
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
        <div className="project-info-fields">
          {activeTab === "info" && renderInfoTab()}
          {activeTab === "nested" && nestedKey && renderNestedTab()}
        </div>
      </ModalShell>

      {editingPath && activeNode && (
        <TaskDraftModal
          node={activeNode}
          readOnly={readOnly}
          onChange={(next) => {
            setNested(updateNodeByPath(nestedItems, editingPath, () => next));
          }}
          onDelete={
            readOnly
              ? null
              : () => {
                  setNested(deleteNodeByPath(nestedItems, editingPath));
                  setEditingPath(null);
                }
          }
          onClose={() => setEditingPath(null)}
        />
      )}
    </>
  );
}
